import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Button from '../ui/Button';
import Skeleton from '../ui/Skeleton';
import AuthLayout, { PasswordField } from '../shell/AuthLayout';

// HD-4: Portal profile on src/ui (DESIGN rev 2.1 §3.3). Same token check and reset as before.
export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/api/auth/reset-password/${token}`)
      .then(r => setUserInfo(r.data))
      .catch(e => { if (e.response?.status === 410) setExpired(true); })
      .finally(() => setLoading(false));
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Use at least 8 characters.'); return; }
    if (password !== confirm) { setErr('The two passwords do not match.'); return; }
    setSaving(true);
    try {
      await api.post(`/api/auth/reset-password/${token}`, { password });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.error || 'The password did not save. Check the connection and try again.');
    } finally { setSaving(false); }
  }

  if (loading) {
    return (
      <AuthLayout>
        <p className="ui-auth__lede" role="status">Checking your reset link</p>
        <Skeleton width="100%" /><Skeleton width="60%" />
      </AuthLayout>
    );
  }
  if (expired) {
    return (
      <AuthLayout>
        <h1 className="ui-auth__title">This link has expired</h1>
        <p className="ui-auth__lede">Reset links work once and last 1 hour. Request a new one and use it straight away.</p>
        <Button variant="primary" portal onClick={() => navigate('/forgot-password')}>Request a new link</Button>
      </AuthLayout>
    );
  }
  if (done) {
    return (
      <AuthLayout>
        <h1 className="ui-auth__title">Password updated</h1>
        <p className="ui-auth__lede">Sign in with your new password.</p>
        <Button variant="primary" portal onClick={() => navigate('/login')}>Go to sign in</Button>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout>
      <h1 className="ui-auth__title">Set a new password</h1>
      <p className="ui-auth__lede">{userInfo?.name ? `${userInfo.name}, choose` : 'Choose'} a new password for your account.</p>
      {userInfo?.email && <p className="ui-auth__meta">Account: {userInfo.email}</p>}
      <form className="ui-auth__form" onSubmit={handleSubmit}>
        <PasswordField label="New password" value={password} onChange={e => setPassword(e.target.value)} hint="At least 8 characters" autoFocus autoComplete="new-password" />
        <PasswordField label="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
        {err && <p className="ui-auth__alert" role="alert">{err}</p>}
        <Button type="submit" variant="primary" portal loading={saving}>Reset password</Button>
      </form>
    </AuthLayout>
  );
}
